import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, createStyles } from '@material-ui/core/styles'
import Collapse from '@material-ui/core/Collapse'
import IconExpandLess from '@material-ui/icons/ExpandLess'
import IconExpandMore from '@material-ui/icons/ExpandMore'

import SideMenuItemComponent from './SideMenuItemComponent';
import { List, ListItemIcon, ListItemText } from './styles';


// React runtime PropTypes
export const AppMenuItemPropsTypes = {
  id: PropTypes.number,
  name: PropTypes.string.isRequired,
  path: PropTypes.string,
  icon: PropTypes.elementType,
  roles: PropTypes.array,
  items: PropTypes.array,
}

interface Menu {
  id: number,
  name: string,
  path?: string,
  icon: JSX.Element,
  items?: Menu[],
  roles: string[],
}

interface MenuItemProps extends Menu {
  expanded: boolean,
  onDrawer?: () => void
}

// TypeScript compile-time props type, infered from propTypes
// https://dev.to/busypeoples/notes-on-typescript-inferring-react-proptypes-1g88
type AppMenuItemPropTypes = PropTypes.InferProps<typeof AppMenuItemPropsTypes>
type AppMenuItemPropsWithoutItems = Omit<AppMenuItemPropTypes, 'items'>

// Improve child items declaration
export type AppMenuItemProps = AppMenuItemPropsWithoutItems & {
  items?: AppMenuItemProps[]
}

const AppMenuItem: React.FC<MenuItemProps> = props => {
  const { name, path, icon: Icon, items = [], expanded, onDrawer } = props;

  const classes = useStyles()
  const isExpandable = items && items.length > 0
  const [open, setOpen] = React.useState(false)

  function handleClick() {
    setOpen(!open)

    if (!isExpandable && onDrawer) {
      onDrawer();
    }
  }

  const MenuItemRoot = (
    <SideMenuItemComponent
      className={classes.menuItem}
      link={path}
      name={name}
      open={expanded}
      onClick={handleClick}
    >
      {/* Display an icon if any */}
      {!!Icon && (
        <ListItemIcon style={expanded ? {} : { marginRight: '0' }}>
          {Icon}
        </ListItemIcon>
      )}
      {expanded && (
        <ListItemText primary={name} inset={!Icon} />
      )}
      {/* Display the expand menu if the item has children */}
      {isExpandable && !open && (
        <ListItemIcon style={{ marginRight: expanded ? '8px' : '0' }}>
          <IconExpandMore style={{ fontSize: '16px' }} />
        </ListItemIcon>
      )}
      {isExpandable && open && (
        <ListItemIcon style={{ marginRight: expanded ? '8px' : '0' }}>
          <IconExpandLess style={{ fontSize: '16px' }} />
        </ListItemIcon>
      )}
    </SideMenuItemComponent>
  )

  const MenuItemChildren = isExpandable ? (
    <Collapse in={open} className={classes.collapse} timeout="auto" unmountOnExit>
      <List>
        {items.map((item, index) => (
          <AppMenuItem {...item} key={index} expanded={expanded} />
        ))}
      </List>
    </Collapse>
  ) : null

  return (
    <>
      {MenuItemRoot}
      {MenuItemChildren}
    </>
  )
}

const useStyles = makeStyles(theme =>
  createStyles({
    menuItem: {
      height: '40px',
      transition: 'all 0.2s',
      // @media(min-width: 1920px) {
      //   height: 48px;
      // }      
      "&.MuiButtonBase-root": {
        borderRadius: '4px',
        marginBottom: '8px',
        paddingRight: '0',
        paddingTop: '0',
        paddingBottom: '0'
      },
      "& .MuiListItemIcon-root": {
        minWidth: 'auto',
        marginRight: '8px'
      },
      "& .MuiTypography-body1 ": {
        fontSize: '1.4rem',
        // @media(min-width: 1920px) {
        //   font-size: 1.6rem;
        // } 
      },
      // "& .MuiSvgIcon-root": {
      //   color: "var(--grayLv4)"
      // },
      "&:hover": {
        background: 'var(--grayLv4)',
        "& .MuiTypography-body1": {
          color: "var(--white)"
        },
        "& .MuiListItemIcon-root": {
          color: 'var(--white)'
        },
        // "& .MuiSvgIcon-root": {
        //   color: "var(--white)"
        // },        
      },
      "&.MuiListItem-root.Mui-selected, .MuiListItem-root.Mui-selected:hover": {
        background: 'var(--gradientGray)',
        "& .MuiTypography-body1": {
          color: 'var(--white)'
        },
        "& .MuiListItemIcon-root": {
          color: 'var(--white)'
        }
      }
    },
    collapse: {
      '& .MuiList-root': {
        marginTop: '0'
      }
    }
  }),
)

export default AppMenuItem;